import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export const PAGE_SIZES = [20, 25, 50, 100, "All"];

// Hook: slices `items` for the current page. Resets to page 1 when the result set shrinks.
export function useTablePagination(items, defaultSize = 25) {
  const [pageSize, setPageSize] = useState(defaultSize);
  const [page, setPage] = useState(1);
  const total = items.length;
  const size = pageSize === "All" ? Math.max(total, 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(total / size));
  useEffect(() => { if (page > pageCount) setPage(1); }, [pageCount, page]);
  const cur = Math.min(page, pageCount);
  const pageItems = useMemo(
    () => (pageSize === "All" ? items : items.slice((cur - 1) * size, cur * size)),
    [items, cur, size, pageSize],
  );
  return { pageItems, pageSize, setPageSize, page: cur, setPage, pageCount, total, size };
}

export function PageSizeSelect({ pageSize, setPageSize, testid = "page-size-select" }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === "All" ? "All" : parseInt(v, 10)); }}>
        <SelectTrigger className="h-9 w-[88px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {PAGE_SIZES.map((s) => <SelectItem key={String(s)} value={String(s)}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="hidden sm:inline">entries per page</span>
    </div>
  );
}

export function TablePagination({ page, setPage, pageCount, total, pageSize, size, testid = "pagination" }) {
  if (total === 0) return null;
  const from = pageSize === "All" ? 1 : (page - 1) * size + 1;
  const to = pageSize === "All" ? total : Math.min(page * size, total);
  return (
    <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2.5 text-sm text-slate-500" data-testid={testid}>
      <span data-testid={`${testid}-info`}>Showing {from}–{to} of {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>
          <ChevronLeft className="h-4 w-4" /> Prev
        </Button>
        <span className="px-1 text-xs text-slate-400" data-testid={`${testid}-page`}>Page {page} of {pageCount}</span>
        <Button variant="outline" size="sm" className="h-8" disabled={page >= pageCount} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
